import { compact } from "lodash";
import moment from "moment";
import { MeetingHeaderFragmentFragment } from "types/graphql-schema";

import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import Loading from "@components/loading/loading";
import { removeCurrentYear } from "@helpers/helpers";

// Builds the option list used by every "browse this meeting's series" dropdown
// (the calendar icon in the meeting header and the floating TOC). Both
// consumers fetch the same `getPreviousMeetingsQuery` data and surface the
// same items — next meeting on top, then past meetings newest-first — styled
// to match the TOC popover (day + date columns, right-aligned Today/Next/Last
// label in tf-header-xs-light).
//
// Paginated contexts pass `onSelect` to inline-load the clicked meeting.
// Non-paginated contexts (extension / sidebar / breadcrumb) pass `getHref`
// instead so the items render as real links — preserving open-in-new-tab,
// middle-click and copy-link — and navigate to the meeting's URL.
export const buildMeetingSeriesDropdownOptions = ({
  pastMeetings,
  nextMeetings,
  activeMeetingId,
  hasMore,
  loadingMore,
  onLoadMore,
  onSelect,
  getHref,
}: {
  pastMeetings: MeetingHeaderFragmentFragment[];
  nextMeetings: MeetingHeaderFragmentFragment[];
  activeMeetingId: number | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (meeting: MeetingHeaderFragmentFragment) => void;
  getHref?: (meeting: MeetingHeaderFragmentFragment) => string;
}): DropdownOptionType[] => {
  const firstMeetingInThePast = pastMeetings.find(
    (m) => !moment().isSame(m.startDatetime, "day"),
  );
  const firstMeetingInFuture = nextMeetings.at(0);

  const meetingOptions: DropdownOptionType[] = compact([
    firstMeetingInFuture,
    ...pastMeetings,
  ]).map((m) => {
    const dateLabel = removeCurrentYear(moment(m.startDatetime).format("ll"));
    const badge = moment().isSame(m.startDatetime, "day")
      ? "Today"
      : moment().isBefore(m.startDatetime, "day")
        ? "Next"
        : firstMeetingInThePast?.id === m.id
          ? "Last"
          : null;
    const label = (
      <span className="flex items-center gap-1">
        <span className="w-14 shrink-0 whitespace-nowrap text-left">
          {dateLabel}
        </span>
        {badge && (
          <span className="ml-auto tf-header-xs-light text-gray-500 whitespace-nowrap">
            {badge}
          </span>
        )}
      </span>
    );
    const className = activeMeetingId === m.id ? "bg-gray-100" : "";
    return getHref
      ? { type: "link" as const, label, className, to: getHref(m) }
      : { label, className, onClick: () => onSelect(m) };
  });

  return compact([
    ...meetingOptions,
    hasMore && { type: "divider" as const },
    hasMore && {
      label: (
        <span className="flex items-center gap-1">
          <span>Load more</span>
          {loadingMore && <Loading mini size="4" />}
        </span>
      ),
      onClick: (e: any) => {
        // react-menu reads `keepOpen` off the top-level event arg (not
        // `syntheticEvent.keepOpen`). Setting it true prevents the menu
        // from closing so the user can keep scrolling through older
        // meetings as they load in.
        if (e) e.keepOpen = true;
        onLoadMore();
      },
    },
  ]);
};
